import {useMutation, useQueryClient} from '@tanstack/react-query';

import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {logger, serializeError} from '../../utils/logger';

import {wishlistKeys} from './keys';
import {WishlistData} from './types';

/**
 * Mutation hook for adding an item to the wishlist
 *
 * Adds the item to the user's wishlist and invalidates the cache
 * to refetch the updated entries list.
 *
 * @returns Mutation object with mutate function and loading state
 *
 * @example
 * const {mutate: addToWishlist, isPending} = useAddToWishlist();
 * addToWishlist(orderLine);
 */
export const useAddToWishlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: OrderLine) => {
      // Get current wishlist data from cache
      let wishlistData = queryClient.getQueryData<WishlistData>(wishlistKeys.list());

      if (!wishlistData?.wishlist.id) {
        // Force refetch if no wishlist in cache
        await queryClient.refetchQueries({queryKey: wishlistKeys.list()});
        wishlistData = queryClient.getQueryData<WishlistData>(wishlistKeys.list());

        if (!wishlistData?.wishlist.id) {
          throw new Error(
            'Unable to load wishlist: cache empty after refetch. User may need to reload the page.',
          );
        }
      }

      if (!item.product_id || !item.product_code) {
        const missing = [!item.product_id && 'product_id', !item.product_code && 'product_code']
          .filter(Boolean)
          .join(', ');
        throw new Error(
          `Cannot add to wishlist: missing ${missing}. Product name: "${item.name || 'unknown'}"`,
        );
      }

      await apiService.addItemToWishlist(
        wishlistData.wishlist.id,
        item.product_id,
        item.product_code,
      );

      logger.info('useAddToWishlist', 'Item added to wishlist', {
        productCode: item.product_code,
      });

      return item;
    },

    onSuccess: async () => {
      // Refetch entries to get the new entry with its server-generated ID
      await queryClient.refetchQueries({queryKey: wishlistKeys.list()});
      // Products will auto-refetch due to changed entry IDs in query key
    },

    onError: (error, item) => {
      logger.error('useAddToWishlist', 'Failed to add item to wishlist', {
        productId: item.product_id,
        productCode: item.product_code,
        productName: item.name,
        ...serializeError(error),
      });
    },
  });
};
